/* pogi-about-joy.js — joy + storytelling layer for the About / story page.
   REVERSIBLE: toggle the section's "Joyful motion" setting off (this file is then
   never loaded) or delete it. SAFE: bails without the .pogi-about--joy hook, fully
   honors prefers-reduced-motion, never leaves content hidden. 2026-06-15. */
(() => {
  const root = document.querySelector('.pogi-about--joy');
  if (!root) return;
  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rnd = (a, b) => a + Math.random() * (b - a);

  /* 1 — scroll reveal ------------------------------------------------- */
  const SEL = [
    '.pogi-about__hero-copy', '.pogi-about__hero-grid > .pogi-about__frame',
    '.pogi-about__chapter-copy', '.pogi-about__chapter-media',
    '.pogi-about__proof-item', '.pogi-about__marquee',
    '.pogi-about__stat', '.pogi-about__quote', '.pogi-about__cqo-card', '.pogi-about__press-logos',
    '.pogi-about__close-copy', '.pogi-about__close-grid > .pogi-about__frame'
  ].join(',');
  const reveals = Array.prototype.slice.call(root.querySelectorAll(SEL));
  const onReveal = (el) => { if (el.classList.contains('pogi-about__stat')) countUp(el); };
  const showAll = () => reveals.forEach((el) => { el.classList.add('is-in'); onReveal(el); });
  if (REDUCE || !('IntersectionObserver' in window)) {
    showAll();
  } else {
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); onReveal(e.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    reveals.forEach((el) => io.observe(el));
    setTimeout(showAll, 3000); // failsafe
  }

  /* 2 — count-up on stat reveal (pure integers only) ----------------- */
  function countUp(el) {
    const n = el.querySelector('.pogi-about__stat-n');
    if (!n || n.dataset.done) return;
    const m = (n.textContent || '').trim().match(/^(\d[\d,]{0,11})(\D{0,4})$/); // number + optional suffix like "+"
    if (!m) return;
    const target = parseInt(m[1].replace(/,/g, ''), 10);
    if (!isFinite(target)) return;
    const suffix = m[2] || '';
    n.dataset.done = '1';
    const render = (v) => { n.textContent = v.toLocaleString() + suffix; };
    if (REDUCE) { render(target); return; }
    const dur = 1150, t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3);
      render(Math.round(e * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* 3 — signature draws itself on reveal (handled in CSS via .is-in) -- */
  // (the .pogi-about__sign element gets .is-in from the close-copy reveal chain)

  // reduced-motion: auto-motion blocks (3.5 holo, 4 timeline) are gated with if (!REDUCE);
  // user-triggered eggs (wag/boop/konami) still bind so deliberate clicks respond.

  /* perf — pause the continuous animations (3 marquee rows + breathing hero) while
     they're off-screen, so a motion-heavy page isn't burning CPU/battery unseen */
  if ('IntersectionObserver' in window) {
    const pauseIO = new IntersectionObserver((ents) => ents.forEach((e) => {
      e.target.classList.toggle('is-offscreen', !e.isIntersecting);
    }), { rootMargin: '160px' });
    root.querySelectorAll('.pogi-about__marquee, .pogi-about__hero-grid > .pogi-about__frame').forEach((el) => pauseIO.observe(el));
  }

  /* 3.5 — CQO foil card: cursor-reactive holo glare + 3D tilt (mouse), gyro tilt (touch) */
  if (!REDUCE) try {
  const holoCard = root.querySelector('.pogi-about__cqo-card');
  if (holoCard) {
    let hRaf = false;
    const setFoil = (px, py) => {
      holoCard.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
      holoCard.style.setProperty('--my', (py * 100).toFixed(1) + '%');
      holoCard.style.setProperty('--rx', ((0.5 - py) * 11).toFixed(2) + 'deg');
      holoCard.style.setProperty('--ry', ((px - 0.5) * 13).toFixed(2) + 'deg');
    };
    const restFoil = () => {
      holoCard.style.setProperty('--mx', '50%'); holoCard.style.setProperty('--my', '50%');
      holoCard.style.setProperty('--rx', '0deg'); holoCard.style.setProperty('--ry', '0deg');
    };
    if (matchMedia('(pointer: fine)').matches) {
      let hX = 0, hY = 0;
      holoCard.addEventListener('pointermove', (e) => { hX = e.clientX; hY = e.clientY; if (!hRaf) { hRaf = true; requestAnimationFrame(() => { hRaf = false; const r = holoCard.getBoundingClientRect(); setFoil(Math.min(1, Math.max(0, (hX - r.left) / r.width)), Math.min(1, Math.max(0, (hY - r.top) / r.height))); }); } });
      holoCard.addEventListener('pointerleave', restFoil);
    }
    if (window.DeviceOrientationEvent && matchMedia('(pointer: coarse)').matches) {
      const onTilt = (e) => {
        if (e.gamma == null) return;
        const gx = Math.max(-1, Math.min(1, e.gamma / 28));
        const gy = Math.max(-1, Math.min(1, ((e.beta || 45) - 45) / 28));
        if (!hRaf) { hRaf = true; requestAnimationFrame(() => { hRaf = false; setFoil(0.5 + gx * 0.5, 0.5 + gy * 0.5); }); }
      };
      const enableGyro = () => {
        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
          DeviceOrientationEvent.requestPermission().then((s) => { if (s === 'granted') window.addEventListener('deviceorientation', onTilt); }).catch(() => {});
        } else { window.addEventListener('deviceorientation', onTilt); }
      };
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        holoCard.addEventListener('click', enableGyro, { once: true }); // iOS needs a tap to grant
      } else { enableGyro(); }
    }
  }
  } catch (eHolo) {}

  /* 4 — timeline spine + Pogi trotting down it (replaces the paw trail) */
  if (!REDUCE) try {
  const chapters = Array.prototype.slice.call(root.querySelectorAll('.pogi-about__chapter'));
  const tail = root.querySelector('.pogi-about__proof');
  const nodes = tail ? chapters.concat([tail]) : chapters;
  if (nodes.length >= 2 && matchMedia('(min-width: 1400px)').matches) {
    const rail = document.createElement('div');
    rail.className = 'pogi-tl';
    rail.setAttribute('aria-hidden', 'true');
    rail.innerHTML = '<div class="pogi-tl__line"><i class="pogi-tl__fill"></i></div>'
      + '<div class="pogi-tl__pup"><svg viewBox="0 0 24 24" fill="currentColor">'
      + '<ellipse cx="12" cy="16.4" rx="5.1" ry="4.1"/><ellipse cx="5.7" cy="11.3" rx="2" ry="2.7"/>'
      + '<ellipse cx="9.9" cy="8.3" rx="2" ry="2.8"/><ellipse cx="14.1" cy="8.3" rx="2" ry="2.8"/>'
      + '<ellipse cx="18.3" cy="11.3" rx="2" ry="2.7"/></svg>'
      + '<img class="pogi-tl__pup-logo" alt="" src="' + (root.getAttribute('data-pogilogo') || '') + '">'
      + '</div>';
    root.appendChild(rail);
    const fill = rail.querySelector('.pogi-tl__fill');
    const pup = rail.querySelector('.pogi-tl__pup');
    let cheered = false;
    const cheer = () => {
      if (cheered || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      cheered = true;
      pup.classList.add('is-arrived');
      const pr = pup.getBoundingClientRect(), rr = rail.getBoundingClientRect();
      const cx = pr.left - rr.left + pr.width / 2, cy = pr.top - rr.top + pr.height / 2;
      const bits = ['🎉', '🎊', '✨', '🐾', '⭐', '💥', '🧡'];
      for (let i = 0; i < 42; i++) {
        const ang = (i / 42) * Math.PI * 2 + rnd(-0.12, 0.12), s = document.createElement('span');
        s.className = 'pogi-tl__spark'; s.textContent = bits[i % bits.length];
        s.style.left = cx + 'px'; s.style.top = cy + 'px';
        s.style.fontSize = (22 + Math.round(rnd(0, 30))) + 'px';
        s.style.setProperty('--dx', Math.round(Math.cos(ang) * rnd(95, 255)) + 'px');
        s.style.setProperty('--dy', Math.round(Math.sin(ang) * rnd(95, 255) - 36) + 'px');
        s.style.animationDelay = (i % 6) * 55 + 'ms';
        rail.appendChild(s);
        s.addEventListener('animationend', () => s.remove(), { once: true });
      }
    };
    const dots = nodes.map((ch) => {
      const isTail = ch === tail;
      const yr = isTail ? '2026' : ((ch.querySelector('.pogi-about__eyebrow')?.textContent || '').match(/\d{4}/) || [''])[0];
      const dot = document.createElement('div');
      dot.className = 'pogi-tl__dot' + (isTail ? ' pogi-tl__dot--now' : '');
      dot.innerHTML = '<b></b><span>' + yr + '</span>';
      rail.appendChild(dot);
      return { ch, dot };
    });
    let top = 0, bottom = 0;
    const layout = () => {
      top = nodes[0].offsetTop;
      const last = nodes[nodes.length - 1];
      bottom = last.offsetTop + last.offsetHeight * (last === tail ? 0.3 : 1);
      rail.style.top = top + 'px';
      rail.style.height = (bottom - top) + 'px';
      dots.forEach(({ ch, dot }) => { dot.style.top = (ch.offsetTop + ch.offsetHeight * 0.3 - top) + 'px'; });
    };
    const update = () => {
      const readLine = window.scrollY + window.innerHeight * 0.5;
      const p = Math.max(0, Math.min(1, (readLine - top) / (bottom - top)));
      fill.style.height = (p * 100) + '%';
      pup.style.top = (p * 100) + '%';
      pup.classList.toggle('is-go', p > 0.001 && p < 0.999);
      dots.forEach(({ ch, dot }) => dot.classList.toggle('is-on', readLine >= ch.offsetTop + ch.offsetHeight * 0.3));
      if (tail) { if (p > 0.99) cheer(); else if (p < 0.5) { cheered = false; pup.classList.remove('is-arrived'); } }
    };
    layout(); update();
    let tlTick = false;
    const onTlScroll = () => { if (!tlTick) { tlTick = true; requestAnimationFrame(() => { update(); tlTick = false; }); } };
    window.addEventListener('scroll', onTlScroll, { passive: true });
    window.addEventListener('resize', () => { layout(); update(); }, { passive: true });
  }
  } catch (eTl) {}

  /* 4.5 — MOBILE celebration: no left-rail timeline under 1400px, so fire a one-time
     Pogi-logo pop + fireworks when the proof band scrolls into view (phones get the payoff). */
  try {
    const proofBand = root.querySelector('.pogi-about__proof');
    if (proofBand && !REDUCE && matchMedia('(max-width: 1399px)').matches && 'IntersectionObserver' in window) {
      const logoUrl = root.getAttribute('data-pogilogo') || '';
      let mfired = false;
      const mobCele = () => {
        if (mfired) return; mfired = true;
        const wrap = document.createElement('div');
        wrap.className = 'pogi-mobcele'; wrap.setAttribute('aria-hidden', 'true');
        if (logoUrl) wrap.innerHTML = '<img class="pogi-mobcele__logo" alt="" src="' + logoUrl + '">';
        proofBand.appendChild(wrap);
        const cx = wrap.getBoundingClientRect().width / 2, cy = 53;
        const bits = ['🎉', '🎊', '✨', '🐾', '⭐', '💥', '🧡'];
        for (let i = 0; i < 30; i++) {
          const ang = (i / 30) * Math.PI * 2 + rnd(-0.12, 0.12);
          const s = document.createElement('span');
          s.className = 'pogi-tl__spark'; s.textContent = bits[i % bits.length];
          s.style.left = cx + 'px'; s.style.top = cy + 'px';
          s.style.fontSize = (20 + Math.round(rnd(0, 24))) + 'px';
          const d = rnd(70, 205);
          s.style.setProperty('--dx', Math.round(Math.cos(ang) * d) + 'px');
          s.style.setProperty('--dy', Math.round(Math.sin(ang) * d) + 'px');
          s.style.animationDelay = (i % 6) * 55 + 'ms';
          wrap.appendChild(s);
        }
        setTimeout(() => wrap.classList.add('is-out'), 1900);
        setTimeout(() => wrap.remove(), 2700);
      };
      const mio = new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting) { mobCele(); mio.disconnect(); }
      }), { threshold: 0.4 });
      mio.observe(proofBand);
    }
  } catch (eMob) {}

  /* 5 — tap-to-wag on the Pogi photos -------------------------------- */
  const wag = (frame, ev) => {
    frame.classList.remove('pogi-wag'); void frame.offsetWidth; frame.classList.add('pogi-wag');
    frame.addEventListener('animationend', function done(e) {
      if (e.animationName === 'pogiWag') { frame.classList.remove('pogi-wag'); frame.removeEventListener('animationend', done); }
    });
    const host = frame.closest('.pogi-about__band') || frame;
    const hr = host.getBoundingClientRect();
    let x, y;
    if (ev && ev.clientX) { x = ev.clientX - hr.left; y = ev.clientY - hr.top; }
    else { const fr = frame.getBoundingClientRect(); x = fr.left - hr.left + fr.width / 2; y = fr.top - hr.top + fr.height / 2; }
    for (let i = 0; i < 6; i++) {
      const ang = (i / 6) * Math.PI * 2, p = document.createElement('span');
      p.className = 'pogi-pawburst'; p.textContent = '🐾';
      p.style.left = x + 'px'; p.style.top = y + 'px';
      p.style.setProperty('--dx', Math.round(Math.cos(ang) * rnd(38, 70)) + 'px');
      p.style.setProperty('--dy', Math.round(-rnd(40, 96)) + 'px');
      p.style.setProperty('--dr', Math.round(rnd(-60, 60)) + 'deg');
      host.appendChild(p);
      p.addEventListener('animationend', () => p.remove(), { once: true });
    }
  };
  root.querySelectorAll('.pogi-about__hero .pogi-about__frame, .pogi-about__close .pogi-about__frame')
    .forEach((frame) => frame.addEventListener('click', (ev) => wag(frame, ev)));

  /* 6 — easter eggs --------------------------------------------------- */
  const toast = (msg) => {
    const t = document.createElement('div'); t.className = 'pogi-egg-toast'; t.textContent = msg;
    document.body.appendChild(t); requestAnimationFrame(() => t.classList.add('on'));
    setTimeout(() => { t.classList.remove('on'); setTimeout(() => t.remove(), 400); }, 2400);
  };
  const rain = (emoji, count) => {
    for (let i = 0; i < count; i++) {
      const p = document.createElement('span'); p.className = 'pogi-rainpaw'; p.textContent = emoji;
      p.style.left = rnd(0, 100) + 'vw';
      p.style.animationDuration = rnd(2.4, 4.4) + 's';
      p.style.animationDelay = rnd(0, 1.2) + 's';
      p.style.fontSize = rnd(16, 30) + 'px';
      document.body.appendChild(p);
      p.addEventListener('animationend', () => p.remove(), { once: true });
    }
  };
  const cqoCard = root.querySelector('.pogi-about__cqo-card');
  const cardGlow = () => { if (cqoCard) { cqoCard.classList.remove('pogi-celebrate'); void cqoCard.offsetWidth; cqoCard.classList.add('pogi-celebrate'); } };
  const dashPup = (emoji) => {
    const d = document.createElement('div'); d.className = 'pogi-dash'; d.textContent = emoji;
    document.body.appendChild(d); d.addEventListener('animationend', () => d.remove(), { once: true });
  };
  const cqoPhoto = root.querySelector('.pogi-about__cqo-photo');
  const spinPup = () => { if (cqoPhoto) { cqoPhoto.classList.remove('pogi-spin'); void cqoPhoto.offsetWidth; cqoPhoto.classList.add('pogi-spin'); } };

  // (a) Konami -> Good Boy Mode  (b) typed keywords: pogi / treat / good boy / woof
  const KONAMI = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
  let kpos = 0, buf = '';
  window.addEventListener('keydown', (e) => {
    kpos = (e.key === KONAMI[kpos] || (e.key && e.key.toLowerCase() === KONAMI[kpos])) ? kpos + 1 : (e.key === KONAMI[0] ? 1 : 0);
    if (kpos === KONAMI.length) { kpos = 0; rain('🐾', 46); toast('🐾 GOOD BOY MODE'); }
    if (e.key && e.key.length === 1) {
      buf = (buf + e.key.toLowerCase()).slice(-12);
      if (buf.endsWith('pogi')) { dashPup('🐕'); toast('Pogi says hi 🐾'); }
      else if (buf.endsWith('treat')) { rain('🦴', 30); toast('Treats incoming! 🦴'); }
      else if (buf.endsWith('good boy') || buf.endsWith('goodboy')) { rain('❤️', 26); cardGlow(); toast("Who's a good boy?! 🥹"); }
      else if (buf.endsWith('woof')) { dashPup('🐶'); toast('Woof woof! 🐶'); }
      else if (buf.endsWith('spin')) { spinPup(); toast('Wheee! 🌀'); }
      else if (buf.endsWith('party')) { rain('🎉', 22); rain('🦴', 10); cardGlow(); toast('PAWTY TIME! 🎉'); }
    }
  });

  // (c) click Pogi's title 5x -> promoted + APPROVED stamp
  const badgeTitle = root.querySelector('.pogi-about__cqo-title');
  if (badgeTitle && cqoCard) {
    let clicks = 0;
    const PROMOS = ['Chief Quality Officer', 'Senior Chief Quality Officer', 'VP of Sniff Testing', 'Head of the Whole Operation', 'Good Boy, Forever'];
    badgeTitle.style.cursor = 'pointer';
    badgeTitle.addEventListener('click', () => {
      clicks++;
      if (clicks <= 4) {
        badgeTitle.textContent = PROMOS[Math.min(clicks, PROMOS.length - 1)];
        badgeTitle.classList.remove('pogi-bump'); void badgeTitle.offsetWidth; badgeTitle.classList.add('pogi-bump');
      }
      if (clicks === 5 && !cqoCard.querySelector('.pogi-stamp')) {
        const stamp = document.createElement('div'); stamp.className = 'pogi-stamp'; stamp.textContent = 'APPROVED';
        cqoCard.appendChild(stamp); cardGlow(); toast('Pawmoted. 🐾');
      }
    });
  }

  // (d) boop the snoot: click Pogi's badge photo
  const boopPhoto = root.querySelector('.pogi-about__cqo-photo');
  if (boopPhoto) {
    let boops = 0;
    boopPhoto.style.cursor = 'pointer';
    boopPhoto.addEventListener('click', () => {
      boops++;
      boopPhoto.classList.remove('pogi-boop'); void boopPhoto.offsetWidth; boopPhoto.classList.add('pogi-boop');
      const b = document.createElement('span'); b.className = 'pogi-boop-txt'; b.textContent = 'boop!';
      boopPhoto.appendChild(b); b.addEventListener('animationend', () => b.remove(), { once: true });
      if (boops === 5) toast('5 boops! 🐽');
      else if (boops === 15) toast('15 boops?! He loves it 🥰');
      else if (boops === 30) { toast('BOOP MASTER 🏆'); rain('🐾', 20); }
    });
  }

  // (e) click HIRED -> service badge; click CLEARANCE -> cycle funny levels
  root.querySelectorAll('.pogi-about__cqo-meta > div').forEach((div) => {
    const dt = div.querySelector('dt'), dd = div.querySelector('dd');
    if (!dt || !dd) return;
    const label = dt.textContent.trim().toLowerCase();
    if (label === 'hired') { div.style.cursor = 'pointer'; div.addEventListener('click', () => toast('🎂 13 years of impeccable service')); }
    if (label === 'clearance') {
      const levels = ['Approves Everything', 'Denies Nothing', 'Sniff-Tested', 'Maximum Floof', 'Top Dog'];
      let li = 0; div.style.cursor = 'pointer';
      div.addEventListener('click', () => { li = (li + 1) % levels.length; dd.textContent = levels[li]; dd.classList.remove('pogi-bump'); void dd.offsetWidth; dd.classList.add('pogi-bump'); });
    }
  });

  // (f) idle 30s -> Pogi naps (zzz floats up)
  if (cqoPhoto) {
    let idleT;
    const nap = () => {
      const z = document.createElement('span'); z.className = 'pogi-zzz'; z.textContent = '💤';
      cqoPhoto.appendChild(z); z.addEventListener('animationend', () => z.remove(), { once: true });
      idleT = setTimeout(nap, 4200);
    };
    const resetIdle = () => { clearTimeout(idleT); idleT = setTimeout(nap, 30000); };
    ['pointermove', 'keydown', 'scroll', 'click', 'touchstart'].forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
    resetIdle();
  }
})();
